import { createFeatureSelector, createSelector } from '@ngrx/store';

import { TenantSettingsKey } from '@core/api/tenant/tenant.enums';
import { selectUserHasEitherPermissions } from '@core/stores/auth/auth.selectors';
import {
  selectTenants as selectRootTenants,
  selectTenantsIsLoading as selectRootTenantsIsLoading,
} from '@core/stores/tenants/tenants.selectors';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { doesSettingsHaveKey } from '@domain/tenant/tenant-models.mappings';

import { createDepotVehicleOverviewCardViewModel } from '@shared/components/depot-overview-card';
import { MovableCardsContainerGroupTypeEnum, MovableCardsContainerViewModel } from '@shared/components/movable-cards-container';

import { buildEnergyCostOverviewMovablePanelViewModel } from '../components/energy-cost-overview-movable-panel/helpers/build-energy-cost-overview-view-model';
import { buildVehicleSocSummaryViewModel } from '../components/vehicle-state-of-charge-summary-movable-panel/helpers/build-vehicle-soc-summary-view-model';
import { MultiSiteDashboardCard } from '../enums/multi-site-dashboard-card.enum';
import {
  mapDashboardOverviewToChargersOverviewMovablePanelViewModel,
  mapDashboardOverviewToSiteFleetsOverviewMovablePanelViewModel,
} from './mappings';
import { MultiSiteDashboardState, multiSiteDashboardFeatureKey } from './multi-site-dashboard.reducer';

const financeAnalyticsPermissions = [PermissionStoreModelEnum.ViewAnyFinanceAnalytics, PermissionStoreModelEnum.ViewOwnFinanceAnalytics];

const filterCards = (
  layout: MovableCardsContainerViewModel<MultiSiteDashboardCard>,
  cardKeys: MultiSiteDashboardCard[]
): MovableCardsContainerViewModel<MultiSiteDashboardCard> => {
  return {
    groups: layout.groups
      .map((group) => {
        if (group.type === MovableCardsContainerGroupTypeEnum.SingleColumn) {
          return { ...group, cards: group.cards.filter((card) => !cardKeys.includes(card.key)) };
        }

        return {
          ...group,
          leftCards: group.leftCards.filter((card) => !cardKeys.includes(card.key)),
          rightCards: group.rightCards.filter((card) => !cardKeys.includes(card.key)),
        };
      })
      .filter((group) =>
        group.type === MovableCardsContainerGroupTypeEnum.SingleColumn
          ? group.cards.length > 0
          : group.leftCards.length > 0 || group.rightCards.length > 0
      ),
  };
};

export const selectMultiSiteDashboardFeature = createFeatureSelector<MultiSiteDashboardState>(multiSiteDashboardFeatureKey);

export const selectTenants = selectRootTenants;
export const selectTenantsAreLoading = selectRootTenantsIsLoading;
export const selectSelectedTenantId = createSelector(selectMultiSiteDashboardFeature, (state) => state.selectedTenantId);

export const selectSelectedTenant = createSelector(selectTenants, selectSelectedTenantId, (tenants, selectedTenantId) =>
  tenants?.find((tenant) => tenant.id === selectedTenantId)
);
export const selectSelectedTenantName = createSelector(selectSelectedTenant, (tenant) => tenant?.name ?? '');
export const selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled = createSelector(selectSelectedTenant, (tenant) =>
  doesSettingsHaveKey(tenant?.tenantSettings ?? [], TenantSettingsKey.WebappDashboardVehicleStateOfChargeSummaryEnabled)
);
export const selectHasMultipleTenants = createSelector(selectTenants, (tenants) => (tenants?.length ?? 0) > 1);

export const selectOverview = createSelector(selectMultiSiteDashboardFeature, (state) => state.overview);
export const selectOverviewIsLoading = createSelector(selectMultiSiteDashboardFeature, (state) => state.overviewIsLoading);

// The multi-site dashboard aggregates only fleet-typed sites, so it always renders the vehicle variant of the card.
export const selectDashboardDepotOverviewCardViewModel = createSelector(selectOverview, (overview) =>
  overview ? createDepotVehicleOverviewCardViewModel(overview.fleetOverview) : undefined
);

// Show the skeleton while either the tenants list or the selected tenant's overview is still loading.
export const selectDashboardDepotOverviewCardIsLoading = createSelector(
  selectOverviewIsLoading,
  selectTenantsAreLoading,
  (overviewIsLoading, tenantsAreLoading) => !!overviewIsLoading || !!tenantsAreLoading
);

export const selectRawCardsLayout = createSelector(selectMultiSiteDashboardFeature, (state) => state.cardsLayout);
export const selectCanViewFinanceAnalytics = selectUserHasEitherPermissions(financeAnalyticsPermissions);
export const selectCardsLayout = createSelector(
  selectRawCardsLayout,
  selectCanViewFinanceAnalytics,
  selectIsSelectedTenantVehicleStateOfChargeSummaryEnabled,
  (layout, canViewFinanceAnalytics, isVehicleStateOfChargeSummaryEnabled) =>
    filterCards(layout, [
      ...(!canViewFinanceAnalytics ? [MultiSiteDashboardCard.EnergyCostOverview] : []),
      ...(isVehicleStateOfChargeSummaryEnabled !== true ? [MultiSiteDashboardCard.VehicleStateOfChargeSummary] : []),
    ])
);

export const selectSiteFleetsOverviewMovablePanelViewModel = createSelector(selectOverview, (overview) =>
  overview ? mapDashboardOverviewToSiteFleetsOverviewMovablePanelViewModel(overview) : undefined
);

// Show skeleton rows while either the tenants list or the selected tenant's overview is still loading.
export const selectSiteFleetsOverviewMovablePanelIsLoading = createSelector(
  selectOverviewIsLoading,
  selectTenantsAreLoading,
  (overviewIsLoading, tenantsAreLoading) => !!overviewIsLoading || !!tenantsAreLoading
);

export const selectChargersOverviewMovablePanelViewModel = createSelector(selectOverview, (overview) =>
  overview ? mapDashboardOverviewToChargersOverviewMovablePanelViewModel(overview) : undefined
);

// Show the skeleton while either the tenants list or the selected tenant's overview is still loading.
export const selectChargersOverviewMovablePanelIsLoading = createSelector(
  selectOverviewIsLoading,
  selectTenantsAreLoading,
  (overviewIsLoading, tenantsAreLoading) => !!overviewIsLoading || !!tenantsAreLoading
);

export const selectEnergyCost = createSelector(selectMultiSiteDashboardFeature, (state) => state.energyCost);
export const selectEnergyCostIsLoading = createSelector(selectMultiSiteDashboardFeature, (state) => state.energyCostIsLoading);
export const selectEnergyCostRange = createSelector(selectMultiSiteDashboardFeature, (state) => state.energyCostRange);

export const selectEnergyCostOverviewMovablePanelViewModel = createSelector(selectEnergyCost, selectEnergyCostRange, (energyCost, range) =>
  energyCost ? buildEnergyCostOverviewMovablePanelViewModel(energyCost, range) : undefined
);

// Show the skeleton while either the tenants list or the selected tenant's energy-cost data is still loading.
export const selectEnergyCostOverviewMovablePanelIsLoading = createSelector(
  selectEnergyCostIsLoading,
  selectTenantsAreLoading,
  (energyCostIsLoading, tenantsAreLoading) => !!energyCostIsLoading || !!tenantsAreLoading
);

export const selectVehicleSocSummary = createSelector(selectMultiSiteDashboardFeature, (state) => state.vehicleSocSummary);
export const selectVehicleSocSummaryIsLoading = createSelector(
  selectMultiSiteDashboardFeature,
  (state) => state.vehicleSocSummaryIsLoading
);
export const selectVehicleSocSummaryRange = createSelector(selectMultiSiteDashboardFeature, (state) => state.vehicleSocSummaryRange);

export const selectVehicleStateOfChargeSummaryMovablePanelViewModel = createSelector(selectVehicleSocSummary, (summary) =>
  summary ? buildVehicleSocSummaryViewModel(summary) : undefined
);

// Show the skeleton while either the tenants list or the selected tenant's summary is still loading.
export const selectVehicleStateOfChargeSummaryMovablePanelIsLoading = createSelector(
  selectVehicleSocSummaryIsLoading,
  selectTenantsAreLoading,
  (vehicleSocSummaryIsLoading, tenantsAreLoading) => !!vehicleSocSummaryIsLoading || !!tenantsAreLoading
);

export const selectSiteAlerts = createSelector(selectMultiSiteDashboardFeature, (state) => state.siteAlerts);
export const selectSiteAlertsIsLoading = createSelector(selectMultiSiteDashboardFeature, (state) => state.siteAlertsIsLoading);
export const selectSiteAlertsSortProperty = createSelector(selectMultiSiteDashboardFeature, (state) => state.siteAlertsSortProperty);
export const selectSiteAlertsSortDirection = createSelector(selectMultiSiteDashboardFeature, (state) => state.siteAlertsSortDirection);

// The service adds the tenant-store loading signal required for site-name resolution.
export const selectSiteAlertsMovablePanelIsLoading = createSelector(
  selectSiteAlertsIsLoading,
  selectTenantsAreLoading,
  (siteAlertsIsLoading, tenantsAreLoading) => !!siteAlertsIsLoading || !!tenantsAreLoading
);
